import React, { useState } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  TablePagination,
  Box,
  Typography,
  alpha,
  Paper,
  TableContainer
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

const PayablesTable = ({ payables, onViewDetails, onMarkPayment, loading, canEdit = true, canDelete = true }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'partial':
        return 'warning';
      case 'unpaid':
      case 'pending':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      bottles: { bg: alpha(theme.palette.info.main, 0.1), text: theme.palette.info.dark },
      caps: { bg: alpha(theme.palette.success.main, 0.1), text: theme.palette.success.dark },
      labels: { bg: alpha(theme.palette.warning.main, 0.15), text: theme.palette.warning.dark },
      raw_materials: { bg: alpha(theme.palette.error.main, 0.1), text: theme.palette.error.dark },
      supplies: { bg: alpha('#0097A7', 0.12), text: '#00695C' }, // Vibrant teal/cyan
      products: { bg: alpha(theme.palette.primary.main, 0.1), text: theme.palette.primary.dark },
      expense: { bg: alpha('#F57C00', 0.1), text: '#E65100' }, // Vibrant orange for expenses
    };
    return colors[category] || { bg: alpha(theme.palette.grey[500], 0.1), text: theme.palette.grey[700] };
  };

  const formatCategory = (category) => {
    // Special case for raw_materials -> Chemicals
    if (category === 'raw_materials') {
      return 'Chemicals';
    }
    const formatted = category?.replace(/_/g, ' ');
    return formatted?.charAt(0).toUpperCase() + formatted?.slice(1);
  };

  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const paginatedPayables = payables.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading payables...</Typography>
      </Box>
    );
  }

  if (payables.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No payables found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ 
        borderRadius: 3, 
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes slideInUp': {
          '0%': {
            transform: 'translateY(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateY(0)',
            opacity: 1,
          }
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <TableContainer sx={{ overflow: 'visible' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Vendor</TableCell>
                <TableCell sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Invoice #</TableCell>
                <TableCell sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Item</TableCell>
                <TableCell sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Category</TableCell>
                <TableCell align="right" sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Total Amount</TableCell>
                <TableCell align="right" sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Paid</TableCell>
                <TableCell align="right" sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Remaining</TableCell>
                <TableCell sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Due Date / Status</TableCell>
                <TableCell align="right" sx={{ 
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  bgcolor: 'white',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  py: 1.5,
                  px: 2.5
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
        <TableBody>
          {paginatedPayables.map((payable) => {
            const daysOverdue = getDaysOverdue(payable.invoice_due_date);
            const isOverdue = daysOverdue > 0 && payable.payment_status !== 'paid';

            return (
              <TableRow 
                key={payable.transaction_id} 
                hover
                onClick={() => onViewDetails(payable)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: isOverdue ? alpha(theme.palette.error.main, 0.05) : 'inherit',
                  '&:hover': { 
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2,
                    px: 2.5
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={400}>
                    {payable.supplier_name || 'Unknown Vendor'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {payable.invoice_file_path && (
                      <ReceiptIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                    <Typography variant="body2">
                      {payable.invoice_number || 'N/A'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                    {payable.item_name || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Qty: {payable.quantity || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatCategory(payable.transaction_type)}
                    size="small"
                    sx={{
                      backgroundColor: getCategoryColor(payable.transaction_type).bg,
                      color: getCategoryColor(payable.transaction_type).text,
                      fontWeight: 500,
                      borderRadius: 1.5
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={400}>
                    {formatCurrency(payable.total_amount)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="success.main">
                    {formatCurrency(payable.paid_amount)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    fontWeight={400}
                    color={payable.remaining_amount > 0 ? 'error.main' : 'text.secondary'}
                  >
                    {formatCurrency(payable.remaining_amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Box>
                      <Typography variant="body2">
                        {formatDate(payable.invoice_due_date)}
                      </Typography>
                      {isOverdue && (
                        <Typography variant="caption" color="error" fontWeight={400}>
                          {daysOverdue} days overdue
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={payable.payment_status?.toUpperCase() || 'UNPAID'}
                      size="small"
                      color={getStatusColor(payable.payment_status)}
                      variant="outlined"
                      sx={{ borderRadius: 1.5, fontWeight: 400 }}
                    />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {payable.payment_status !== 'paid' && canEdit && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkPayment(payable);
                      }}
                      sx={{ 
                        textTransform: 'none',
                        borderRadius: 1.5
                      }}
                    >
                      Mark Payment
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={payables.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default PayablesTable;
